import * as React from 'react'
import { useState } from 'react'
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Chip,
  Button,
  Stack,
  IconButton,
  CircularProgress,
  Box,
  Popover,
  TextField,
} from '@mui/material'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import EditIcon from '@mui/icons-material/Edit'
import { useTheme } from '@mui/material/styles'
import { getDetailAttachments } from '../../services/getDetailAttachments'
import { getBadpressAttachment } from '../../services/getBadpressAttachment'

const formatDate = (dateString) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleDateString('it-IT')
}

const formatAnno = (dateString) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.getFullYear()
}

const rimuoviPrefissoFacts = (testo) => {
  if (!testo) return testo
  return testo.replace(/^\s*facts\s*:\s*/i, '')
}

const NOTA_MAX_LENGTH = 256

const statoConfig = {
  DA_VALIDARE: { label: 'Da validare', color: '#92400E', bg: '#FEF3C7' },
  VALIDATO: { label: 'Validato', color: '#166534', bg: '#DCFCE7' },
  SCARTATO: { label: 'Scartato', color: '#991B1B', bg: '#FEE2E2' },
}

const StatoChip = ({ stato }) => {
  const cfg = statoConfig[stato] || { label: stato, color: '#374151', bg: '#E5E7EB' }
  return (
    <Chip
      label={cfg.label}
      size="small"
      sx={{
        backgroundColor: cfg.bg,
        color: cfg.color,
        fontWeight: 600,
      }}
    />
  )
}

/**
 * Accordion che mostra il dettaglio di un singolo monitoraggio soggetto terzo
 * con la tabella delle notifiche associate.
 *
 * Props:
 * - dettaglioMonitoraggio: { id, idSoggettoTerzo, dataMonitoraggio, notifiche: [...] }
 * - onValida(idMonitoraggio, idNotifica, nota): callback opzionale per chiamare il servizio reale di validazione
 * - onScarta(idMonitoraggio, idNotifica, nota): callback opzionale per chiamare il servizio reale di scarto
 * - defaultExpanded: se true l'accordion parte aperto
 */
const AccordionMonitoraggioSoggettoTerzo = ({
  dettaglioMonitoraggio,
  onValida,
  onScarta,
  defaultExpanded = false,
}) => {
  const theme = useTheme()
  const [notifiche, setNotifiche] = useState(dettaglioMonitoraggio.notifiche || [])
  const [loadingActionId, setLoadingActionId] = useState(null)
  const [dettagliAttachment, setDettagliAttachment] = useState({})
  const [dettagliCaricati, setDettagliCaricati] = useState(false)
  const [loadingDettagli, setLoadingDettagli] = useState(false)
  const [downloadingId, setDownloadingId] = useState(null)
  const [noteDraft, setNoteDraft] = useState({})
  const [noteAnchorEl, setNoteAnchorEl] = useState(null)
  const [noteNotificaId, setNoteNotificaId] = useState(null)

  const handleOpenNota = (event, notifica) => {
    setNoteAnchorEl(event.currentTarget)
    setNoteNotificaId(notifica.id)
  }

  const handleCloseNota = () => {
    setNoteAnchorEl(null)
    setNoteNotificaId(null)
  }

  const handleNotaChange = (notificaId, value) => {
    setNoteDraft((prev) => ({ ...prev, [notificaId]: value }))
  }

  const handleAccordionChange = async (event, expanded) => {
    if (!expanded || dettagliCaricati) return
    const ids = notifiche.map((n) => n.linkArticolo).filter(Boolean)
    if (ids.length === 0) {
      setDettagliCaricati(true)
      return
    }
    setDettagliCaricati(true)
    setLoadingDettagli(true)
    try {
      const evidenze = await getDetailAttachments(ids)
      const dettagliPerId = {}
      ;(evidenze || []).forEach((evidenza) => {
        if (evidenza?.id) dettagliPerId[evidenza.id] = evidenza
      })
      setDettagliAttachment(dettagliPerId)
    } catch (error) {
      console.error('Errore durante il recupero del dettaglio delle notifiche:', error)
    } finally {
      setLoadingDettagli(false)
    }
  }

  const handleDownloadAttachment = async (notifica) => {
    if (!notifica.linkArticolo) return
    setDownloadingId(notifica.id)
    try {
      const response = await getBadpressAttachment(notifica.linkArticolo)
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `${notifica.linkArticolo}_attachment.pdf`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Errore durante il download dell\'allegato:', error)
    } finally {
      setDownloadingId(null)
    }
  }

  const handleValida = async (notifica) => {
    const nota = (noteDraft[notifica.id] || '').trim()
    if (!nota) return
    setLoadingActionId(notifica.id)
    try {
      if (onValida) {
        await onValida(dettaglioMonitoraggio.id, notifica.id, nota)
      }
      setNotifiche((prev) =>
        prev.map((n) => (n.id === notifica.id ? { ...n, stato: 'VALIDATO', nota } : n))
      )
    } catch (error) {
      console.error('Errore durante la validazione della notifica:', error)
    } finally {
      setLoadingActionId(null)
    }
  }

  const handleScarta = async (notifica) => {
    const nota = (noteDraft[notifica.id] || '').trim()
    if (!nota) return
    setLoadingActionId(notifica.id)
    try {
      if (onScarta) {
        await onScarta(dettaglioMonitoraggio.id, notifica.id, nota)
      }
      setNotifiche((prev) =>
        prev.map((n) => (n.id === notifica.id ? { ...n, stato: 'SCARTATO', nota } : n))
      )
    } catch (error) {
      console.error('Errore durante lo scarto della notifica:', error)
    } finally {
      setLoadingActionId(null)
    }
  }

  const notificaInPopover = notifiche.find((n) => n.id === noteNotificaId)

  return (
    <>
    <Accordion
      elevation={0}
      disableGutters
      square={true}
      defaultExpanded={defaultExpanded}
      onChange={handleAccordionChange}
      sx={{ width: '100%', backgroundColor: theme.palette.grey.dark }}
    >
      <AccordionSummary
        expandIcon={<ArrowDropDownIcon />}
        sx={{ backgroundColor: theme.palette.grey.dark }}
        aria-controls={`monitoraggio-${dettaglioMonitoraggio.id}-content`}
        id={`monitoraggio-${dettaglioMonitoraggio.id}-header`}
      >
        <Typography sx={{ fontWeight: 'bold', fontSize: '18px' }}>
          {formatAnno(dettaglioMonitoraggio.dataMonitoraggio)}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ backgroundColor: theme.palette.grey.light }}>
        <TableContainer component={Paper} elevation={0}>
          <Table size="small" sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, width: '10%' }}>Fonte</TableCell>
                <TableCell sx={{ fontWeight: 700, width: '9%' }}>Data notizia</TableCell>
                <TableCell sx={{ fontWeight: 700, width: '20%' }}>Titolo/Descrizione</TableCell>
                <TableCell sx={{ fontWeight: 700, width: '22%' }}>Link</TableCell>
                <TableCell sx={{ fontWeight: 700, width: '9%' }}>Stato</TableCell>
                <TableCell sx={{ fontWeight: 700, width: '17%' }}>Azione</TableCell>
                <TableCell sx={{ fontWeight: 700, width: '13%' }}>Allegato</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingDettagli ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <CircularProgress size={28} />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                <>
              {notifiche.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: 'center', color: 'text.secondary' }}>
                    Nessuna notifica presente
                  </TableCell>
                </TableRow>
              )}
              {notifiche.map((notifica) => {
                const evidenza = dettagliAttachment[notifica.linkArticolo]
                const titolo = rimuoviPrefissoFacts(evidenza?.rationale?.[0]?.offence || evidenza?.rationale?.[0]?.details)
                const link = evidenza?.rationale?.[0]?.links?.[0]?.link
                return (
                <TableRow key={notifica.id}>
                  <TableCell>{notifica.idTestata}</TableCell>
                  <TableCell>{formatDate(notifica.dataRiferimento)}</TableCell>
                  <TableCell
                    sx={{
                      wordBreak: 'break-word',
                      ...(!titolo && { color: 'text.disabled' }),
                    }}
                  >
                    {titolo || '—'}
                  </TableCell>
                  <TableCell sx={{ wordBreak: 'break-word' }}>
                    {link ? (
                      <a href={link} target="_blank" rel="noopener noreferrer">
                        {link}
                      </a>
                    ) : (
                      <Typography component="span" sx={{ color: 'text.disabled' }}>—</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <StatoChip stato={notifica.stato} />
                  </TableCell>
                  <TableCell>
                    {notifica.stato === 'DA_VALIDARE' ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<CheckIcon />}
                          disabled={loadingActionId === notifica.id || !(noteDraft[notifica.id] || '').trim()}
                          sx={{
                            backgroundColor: '#16A34A',
                            '&:hover': { backgroundColor: '#15803D' },
                          }}
                          onClick={() => handleValida(notifica)}
                        >
                          Valida
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<CloseIcon />}
                          disabled={loadingActionId === notifica.id || !(noteDraft[notifica.id] || '').trim()}
                          sx={{
                            backgroundColor: '#DC2626',
                            '&:hover': { backgroundColor: '#B91C1C' },
                          }}
                          onClick={() => handleScarta(notifica)}
                        >
                          Scarta
                        </Button>
                        <IconButton
                          size="small"
                          onClick={(e) => handleOpenNota(e, notifica)}
                          title="Inserisci nota (obbligatoria)"
                        >
                          <EditIcon fontSize="small" color={(noteDraft[notifica.id] || '').trim() ? 'primary' : 'inherit'} />
                        </IconButton>
                      </Stack>
                    ) : (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                          Già gestita
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={(e) => handleOpenNota(e, notifica)}
                          title="Visualizza nota"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    )}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      disabled={!notifica.linkArticolo || downloadingId === notifica.id}
                      onClick={() => handleDownloadAttachment(notifica)}
                      title="Scarica allegato"
                    >
                      <FileDownloadIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
                )
              })}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </AccordionDetails>
    </Accordion>
    <Popover
      open={Boolean(noteAnchorEl)}
      anchorEl={noteAnchorEl}
      onClose={handleCloseNota}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
    >
      <Box sx={{ p: 2, width: 320 }}>
        {notificaInPopover && notificaInPopover.stato === 'DA_VALIDARE' ? (
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Nota (obbligatoria)"
            value={noteDraft[notificaInPopover.id] || ''}
            onChange={(e) => handleNotaChange(notificaInPopover.id, e.target.value.slice(0, NOTA_MAX_LENGTH))}
            inputProps={{ maxLength: NOTA_MAX_LENGTH }}
            helperText={`${(noteDraft[notificaInPopover.id] || '').length}/${NOTA_MAX_LENGTH}`}
          />
        ) : (
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Nota"
            value={notificaInPopover?.nota || ''}
            InputProps={{ readOnly: true }}
          />
        )}
      </Box>
    </Popover>
    </>
  )
}

export default AccordionMonitoraggioSoggettoTerzo
